<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form ref="form" v-model="formData['valid']" class="mb-3" @submit.prevent="updateDevice">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Update {{ getTitle }}</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="12" sm="6">
                            <company-select v-model="formData['company_id']" :items="companies" density="compact" label="Select Company"
                                      clearable @update:model-value="resetDevice"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['product_sku_id']" :items="productSkus"
                                      :rules="[vRules.required]" density="compact" item-title="sku" item-value="id"
                                      label="Product SKU" @update:model-value="formData['portal_id'] = null">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item :subtitle="`Name: ${item.raw.product_name}`" v-bind="props"/>
                                </template>
                            </v-select>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['portal_id']"
                                      :items="selectedProductSku?.portals??[]"
                                      :rules="[vRules.required]" density="compact" item-title="name"
                                      item-value="id" label="Portal"/>
                        </v-col>

                        <v-col v-if="deviceType && deviceType.toLowerCase() === 'harvester'" cols="12" sm="6">
                            <v-select v-model="formData['harvester_site_id']" :items="harvesterSites" density="compact"
                                      item-title="name" item-value="id" label="Harvester Site"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['status']" :items="deviceStatuses" density="compact"
                                      item-title="name" item-value="value" label="Device Status"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['subscription_plan_id']" :items="plans" density="compact"
                                      item-title="name" item-value="id" label="Default Subscription Plan"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['slno']" autofocus :rules="[vRules.required, vRules.minLength(5)]"
                                          density="compact" inputmode="numeric" label="Serial Number"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['aes_key']"
                                          :rules="[vRules.required, vRules.exactLength(32)]" density="compact"
                                          label="AES KEY"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-autocomplete v-model="formData['sim_card_id']"
                                            :disabled="!selectedProductSku?.cellular" :items="simCards"
                                            :rules="selectedProductSku?.cellular?[vRules.required]:[]"
                                            density="compact" item-title="iccid" item-value="id" label="Sim Card"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['eth_mac']"
                                          :disabled="!selectedProductSku?.ethernet"
                                          :rules="selectedProductSku?.ethernet?[vRules.required, vRules.exactLength(15)]:[]"
                                          density="compact" label="ETH MAC"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['imei']"
                                          :disabled="!selectedProductSku?.cellular"
                                          :rules="selectedProductSku?.cellular?[vRules.required, vRules.exactLength(15)]:[]"
                                          density="compact" inputmode="numeric" label="IMEI"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['bt_mac']"
                                          :disabled="!selectedProductSku?.ble"
                                          :rules="selectedProductSku?.ble?[vRules.required, vRules.exactLength(12)]:[]"
                                          density="compact" label="BT MAC"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['wifi_mac']"
                                          :disabled="!selectedProductSku?.wifi"
                                          :rules="selectedProductSku?.wifi?[vRules.required, vRules.exactLength(12)]:[]"
                                          density="compact" label="WIFI MAC"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['lora_mac']"
                                          :disabled="!selectedProductSku?.lora"
                                          :rules="selectedProductSku?.lora?[vRules.required, vRules.exactLength(16)]:[]"
                                          density="compact" label="LORA MAC"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['cpuid_1']"
                                          :disabled="!selectedProductSku?.cpuid_1"
                                          :rules="selectedProductSku?.cpuid_1?[vRules.required, vRules.maxLength(32)]:[]"
                                          density="compact" label="CPU ID 1"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['cpuid_2']"
                                          :disabled="!selectedProductSku?.cpuid_2"
                                          :rules="selectedProductSku?.cpuid_2?[vRules.required, vRules.maxLength(32)]:[]"
                                          density="compact" label="CPU ID 2"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {send} from "@/composables/apiClient";
import {ref, reactive, onMounted, computed} from "vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useLoadingStore} from "@/stores/loadingStore";
import CompanySelect from "@/components/globals/company-select.vue";

const formReady = ref(false);
const snackbar = useSnackbarStore();
const loadingStore = useLoadingStore();
const deviceDetails = ref(null);
const props = defineProps({
    modelValue: Boolean,
    device: {
        type: Object
    },
    deviceType: {
        type: String,
        default: null,
    },
});
const deviceStatuses = [
    {
        value: 0,
        name: 'Active'
    },
    {
        value: 1,
        name: 'Disabled'
    },
    {
        value: 2,
        name: 'Faulty'
    },
    {
        value: 3,
        name: 'Lost'
    }
];
const formData = reactive({
    valid: false,
    slno: null,
    imei: null,
    bt_mac: null,
    wifi_mac: null,
    eth_mac: null,
    lora_mac: null,
    cpuid_1: null,
    cpuid_2: null,
    aes_key: null,
    portal_id: null,
    company_id: null,
    sim_card_id: null,
    product_sku_id: null,
    harvester_site_id: null,
    subscription_plan_id: null,
    status: null,
});
const emits = defineEmits(['update:modelValue', 'updateDevice']);
const dialogSwitch = ref(props.modelValue);
const harvesterSites = ref([]);
const productSkus = ref([]);
const companies = ref([]);
const simCards = ref([]);
const errors = ref([]);
const plans = ref([]);
const form = ref();

const selectedProductSku = computed(() => {
    return productSkus.value.find((sku) => sku.id === formData['product_sku_id']);
});

onMounted(async () => {
    await fetchDeviceDetails();
    const promises = [fetchPlans(), fetchSimCards(), fetchCompaniesList(), fetchProductSkusList()];
    if (props.deviceType && props.deviceType.toLowerCase() === 'harvester') {
        promises.push(fetchHarvesterSites());
    }
    Promise.all(promises).finally(() => formReady.value = true);
});

const getTitle = computed(() => {
    switch (props.deviceType) {
        case "harvester":
            return "Harvester Device";
        case "personal":
            return "Personal Tracker";
        default:
            return "Device";
    }
})

const updateFormData = (data) => {
    for (let prop in formData) {
        if (prop !== 'valid') {
            formData[prop] = data[prop];
        }
    }
}
const resetDevice = () => {
    const confirmation = confirm("Are you sure to change company? This will trigger sequence of operations to reset the device.");
    if (!confirmation) {
        formData['company_id'] = props.device.company_id;
        return;
    }
    formData['harvester_site_id'] = null;
    (props.deviceType && props.deviceType.toLowerCase() === 'harvester') ? fetchHarvesterSites() : ''
}
const fetchCompaniesList = () => {
    return send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Companies List could not be loaded', 'error')
    })
}
const fetchProductSkusList = () => {
    return send('get', `product-skus`, {
        list: true,
        portals: true,
        type: props.deviceType,
        columns: JSON.stringify("id,sku,product_name,ble,wifi,lora,cellular,cpuid_1,cpuid_2")
    })
        .then((response) => {
            productSkus.value = response.data.data.product_skus;
        }).catch((error) => {
        snackbar.showSnackbar('Product skus list could not be loaded', 'error')
    })
}
const fetchPlans = () => {
    return send('get', `subscription-plans/list`)
        .then((response) => {
            plans.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Subscription plans List could not be loaded', 'error')
    })
}
const fetchSimCards = () => {
    return send('get', `admin/sim-cards?list=1&available=1&device=${props.device.id}`)
        .then((response) => {
            simCards.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Sim cards List could not be loaded', 'error')
    })
}
const fetchHarvesterSites = () => {
    return send('get', `harvesters/sites`, {list: true, company: formData['company_id']})
        .then((response) => {
            harvesterSites.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Harvester Sites List could not be loaded', 'error')
    })
}
const fetchDeviceDetails = () => {
    return send('get', `devices/${props.device.id}`)
        .then((response) => {
            updateFormData(response.data.data);
        }).catch((error) => {
            snackbar.showSnackbar('Sim cards List could not be loaded', 'error')
        })
}
const updateDevice = () => {
    form.value.validate().then((resp) => {
        if (resp.valid) {
            errors.value = [];
            loadingStore.showLoading('Updating...', 'Please wait while we save your changes');
            if (props.device.company_id !== formData['company_id']) {
                const choice = confirm("You have changed the company, it will trigger WIPE command to device. Are you sure to proceed?")
                if (!choice) {
                    loadingStore.hideLoading();
                    document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
                    return
                }
            }
            send('put', `devices/${props.device.id}`, formData)
                .then((response) => {
                    snackbar.showSnackbar(response.data.message);
                    if (response.data.success) {
                        emits('updateDevice', response.data.data);
                        emits('update:modelValue', false);
                    }
                })
                .catch(function (errorResponse) {
                    if (errorResponse.response.data.errors) {
                        let validationErrors = errorResponse.response.data.errors;
                        Object.keys(validationErrors).forEach((key) => {
                            errors.value.push(validationErrors[key][0]);
                        });
                    } else if (errorResponse.response.data.message) {
                        errors.value.push(errorResponse.response.data.message);
                    }
                    snackbar.showSnackbar('Something went wrong', 'error');
                    document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
                }).finally(() => loadingStore.hideLoading())
        } else {
            snackbar.showSnackbar('Some required field(s) are missing', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        }
    });
}

</script>
